<script>
	import { has_access, get_user_slug } from '#lib/util.js';
  import { page } from '$app/state';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import NotificationBadge from '#lib/NotificationBadge.svelte';
  import Divider from '#lib/Divider.svelte';
  import Avatar from '#lib/Avatar.svelte';
  import t from '#lib/i18n.svelte.js';
  import { getContext } from 'svelte';
  import AccountSwitcher from '#lib/AccountSwitcher.svelte';
  import { register_click_outside_element } from '#lib/click_outside.svelte.js';

  let site_settings = getContext('site_settings');
  let menu_container = $state();
  let is_open = $state(false);

  $effect(() => {
    if (is_open && menu_container) {
      const unregister = register_click_outside_element({
        el: menu_container,
        on_click_outside: () => {
          is_open = false;
        }
      });
      return unregister;
    }
  });

  // Show tasks if there's an open task
  let open_tasks_count = $derived.by(() => {
    return [
    ].reduce((a, b) => a + b, 0);
  });
  let notification_count = $derived.by(()=>{
    return [
      page.data.unread_conversations,
      open_tasks_count
    ].reduce((a, b) => a + b, 0);
  });

</script>

<div class="account-menu-container relative flex-row items-center" bind:this={menu_container}>
  <button 
    class="account-menu-button"
    onclick={() => is_open = !is_open}
    aria-expanded={is_open}
    aria-haspopup="true"
    aria-label={t('account_menu')}
    style='--button-outer-block-spacing: var(--default-button-outer-block-spacing)'
  >
    <ButtonLabel 
      align='center' 
      text_style='text-control'
      variant='brand ghostly text-compact'
    >
      {#snippet start_icon()}
        <Avatar
          avatar={page.data.current_user.avatar}
          name={page.data.current_user.name}
          animate={true}
        />
      {/snippet}
    </ButtonLabel>
    {#if notification_count > 0 && !is_open}
      <NotificationBadge>{notification_count}</NotificationBadge>
    {/if}
  </button>


  {#if is_open}
    <div class='absolute-popover flex-column' role='dialog'>
      <!-- Messages -->
      <a
        href='/messages'
        class='messages-link flex-row'
        title={t('messages')}
        data-sveltekit-preload-data="false"
      >
        <ButtonLabel 
          variant='brand ghostly' 
          start_icon='paperplane' 
          align='start-stretch'
          text_style='text-control'
          text={t('messages')}
        >
        {#snippet end_icon()}
          {#if page.data.unread_conversations > 0}
            <NotificationBadge placement='inline-end'>{page.data.unread_conversations}</NotificationBadge>
          {/if}  
        {/snippet}
      </ButtonLabel>
      </a>

      <!-- My Event Registrations -->
      <a href='/account/my-event-registrations'>
        <ButtonLabel 
          variant='brand ghostly' 
          start_icon='calendar'
          align='start-stretch'
          text_style='text-control'
        >
          {t('my_event_registrations')}
        </ButtonLabel>
      </a>

      <!-- Invoices: the Stripe customer portal, once the member has a Stripe
           customer. No preload: every visit opens a portal session. -->
      {#if page.data.current_user?.stripe_customer_id}
        <a href='/create-customer-portal-session' data-sveltekit-preload-data='false' data-sveltekit-reload>
          <ButtonLabel
            variant='brand ghostly'
            start_icon='invoice'
            align='start-stretch'
            text_style='text-control'
          >
            {t('invoices')}
          </ButtonLabel>
        </a>
      {/if}

      <!-- My Collections -->
      {#if has_access('member', page.data.current_user?.role)}
        <a 
          href={`/${get_user_slug(page.data.current_user.user_id, page.data.current_user.name)}/collections`} 
        >
          <ButtonLabel 
            variant='brand ghostly' 
            start_icon='bookmarks'
            align='start-stretch'
            text_style='text-control'
          >
            {t('my_collections')}
          </ButtonLabel>
        </a>
      {/if}

      <!-- Tasks Section -->
      {#if open_tasks_count > 0}
        <Divider class='px p-2'>
          <div class='flex-row text-control text-3 items-center'>
            {t('open_tasks')}
            <NotificationBadge placement='inline-end'>{open_tasks_count}</NotificationBadge>
          </div>  
        </Divider>
        <div class="account-tasks flex-column">
          <!-- List of open tasks ... -->
        </div>
      {/if}
      
      <Divider class='px p-4' label={t('settings')} />

      <!-- Account settings -->
      <a 
        href='/account/settings'
        title={t('account')}
      >
        <ButtonLabel 
          variant='brand ghostly' 
          start_icon='calm-face' 
          align='start-stretch'
          text_style='text-control'
        >{t('account_settings')}</ButtonLabel>
      </a>
  
      {#if page.data.current_user.role === 'admin'}
        <a 
          href={`/settings`} 
          data-sveltekit-preload-data="false" 
          data-sveltekit-reload
        >
          <ButtonLabel 
            align='start-stretch'
            start_icon='settings'
            text_style='text-control'
            variant='brand ghostly'
          >
            {t('website')}
          </ButtonLabel>
        </a>
      {/if}

      {#if page.data.can_use_email_signature}
        <a href='/signature'>
          <ButtonLabel
            align='start-stretch'
            start_icon='email'
            text_style='text-control'
            variant='brand ghostly'
          >
            {t('my_email_signature')}
          </ButtonLabel>
        </a>
      {/if}

      <Divider />
      <a 
        href={`/logout?return_to=${encodeURIComponent(page.url.pathname.substring(1))}`} 
        data-sveltekit-preload-data="false"
        data-sveltekit-reload
      >
        <ButtonLabel 
          align='start-stretch' 
          start_icon='exit'
          variant='brand ghostly'
          text_style='text-control'
        >
          {t('logout')}
        </ButtonLabel>
      </a>

      {#if page.data.account_switcher_users?.length > 1 || page.data.current_user.role === 'admin'}
        <Divider class='px p-4' label={t('switch_account')} />
        <div class='px py p-2'>
          <AccountSwitcher 
            container='inline'
            on_switch_user={() => {
              is_open = false;
            }} 
          />
        </div>
      {/if}

    </div>
  {/if}
</div>

<style lang="postcss">
  :global(.account-menu-button .avatar) {
    border-radius: 100%;
  }
  .account-menu-container :global(.account-switcher .select-list) {
    max-height: none;
  }
  /* Match menu item contrast: muted tokens (--text-3, --color-default-stroke,
     --color-default-foreground-300) stay too dim on dark menu surfaces. */
  .absolute-popover {
    color: var(--color-foreground, var(--color-default-foreground));
    --icon-color1: var(--color-foreground, var(--color-default-foreground));
    --divider-color: color-mix(
      in oklch,
      var(--color-foreground, var(--color-default-foreground)) 28%,
      transparent
    );
  }
  .absolute-popover :global(.divider > span) {
    color: color-mix(
      in oklch,
      var(--color-foreground, var(--color-default-foreground)) 70%,
      transparent
    );
  }
</style>